import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllGroupChats,
  fetchChatById,
  createGroupChat,
  sendChatMessage,
  fetchMoreMessages,
  deleteChatMessage,
  editChatMessage,
} from "./thunk";

const sortMessagesDesc = (messages = []) =>
  [...messages].sort((a, b) => {
    const dateA = new Date(a?.createdAt || a?.time || 0).getTime();
    const dateB = new Date(b?.createdAt || b?.time || 0).getTime();
    return dateB - dateA;
  });

const mergeMessagesLatestFirst = (primary = [], secondary = []) => {
  const mergedMap = new Map();
  [...primary, ...secondary].forEach((msg) => {
    if (!msg || !msg._id) return;
    const key = String(msg._id);
    if (!mergedMap.has(key)) {
      mergedMap.set(key, msg);
    }
  });
  return sortMessagesDesc(Array.from(mergedMap.values()));
};

const upsertMessageCache = (state, chatId, messages = []) => {
  if (!chatId || !Array.isArray(messages) || messages.length === 0) return;
  const key = String(chatId);
  const existing = state.messageCache?.[key] ? [...state.messageCache[key]] : [];
  const mergedMap = new Map();

  [...existing, ...messages].forEach((msg) => {
    if (!msg || !msg._id) return;
    mergedMap.set(String(msg._id), msg);
  });

  const merged = sortMessagesDesc(Array.from(mergedMap.values()));

  state.messageCache = state.messageCache || {};
  state.messageCache[key] = merged;
};

const initialState = {
  groupChats: [],
  loading: false,
  currentChat: {
    messages: [],
    members: [],
    page: 1,
    hasMore: true,
  },
  error: null,
  typingUsers: {},
  lastMessageId: null,
  unread: {},
  activeChatId: null,

  // Pagination states for groups
  hasMoreGroups: true,
  groupsPage: 1,
  messageCache: {},
  totalGroups: 0,
};

const chatSlice = createSlice({
  name: "Chat",
  initialState,
  reducers: {
    clearChatError(state) {
      state.error = null;
    },
    clearCurrentChat(state) {
      state.currentChat = { messages: [], members: [], page: 1, hasMore: true };
      state.typingUsers = {};
      state.lastMessageId = null;
    },
    setActiveChatId(state, action) {
      state.activeChatId = action.payload || null;
    },
    setTypingUser(state, action) {
      const { chatId, userId, isTyping } = action.payload;
      if (!state.typingUsers[chatId]) {
        state.typingUsers[chatId] = [];
      }
      if (isTyping) {
        if (!state.typingUsers[chatId].includes(userId)) {
          state.typingUsers[chatId].push(userId);
        }
      } else {
        state.typingUsers[chatId] = state.typingUsers[chatId].filter(id => id !== userId);
      }
    },
    addNewMessage(state, action) {
      const newMessage = action.payload;
      const resolvedChatId =
        newMessage.chatId ||
        newMessage.chatRequestId ||
        state.currentChat?._id ||
        state.currentChat?.chatRequestId;

      if (
        state.currentChat._id === (newMessage.chatId || newMessage.chatRequestId) ||
        state.currentChat.chatRequestId === (newMessage.chatId || newMessage.chatRequestId)
      ) {
        const exists = state.currentChat.messages?.some(msg => msg._id === newMessage._id);
        if (!exists) {
          state.currentChat.messages = [...(state.currentChat.messages || []), newMessage];
          state.lastMessageId = newMessage._id;
        }
      }

      if (resolvedChatId) {
        upsertMessageCache(state, resolvedChatId, [newMessage]);
      }
    },
    updateMessages(state, action) {
      const { messages, lastMessageId } = action.payload;
      if (messages && Array.isArray(messages)) {
        state.currentChat.messages = messages;
        state.lastMessageId = lastMessageId || messages[messages.length - 1]?._id || null;

        const resolvedChatId =
          state.currentChat?._id ||
          state.currentChat?.chatRequestId ||
          state.currentChat?.groupId;

        if (resolvedChatId) {
          upsertMessageCache(state, resolvedChatId, messages);
        }
      }
    },
    updateChatListOrder(state, action) {
      const chatId = action.payload;
      if (!chatId || !Array.isArray(state.groupChats)) return;
      const index = state.groupChats.findIndex((c) => {
        const id = String(chatId);
        return (
          String(c._id) === id ||
          String(c.chatRequestId) === id ||
          String(c.groupId) === id
        );
      });
      if (index > -1) {
        const [chat] = state.groupChats.splice(index, 1);
        state.groupChats.unshift(chat);
      }
    },
    incrementUnread(state, action) {
      const chatId =
        typeof action.payload === "object" && action.payload !== null
          ? action.payload.chatId
          : action.payload;
      if (!chatId) return;

      const idx = state.groupChats.findIndex((c) => {
        const id = String(chatId);
        return (
          String(c._id) === id ||
          String(c.chatRequestId) === id ||
          String(c.groupId) === id
        );
      });

      if (idx > -1) {
        const current = state.groupChats[idx].unreadCount || 0;
        state.groupChats[idx].unreadCount = current + 1;

        console.log("📊 [REDUCER] Incremented unread count:", {
          chatId: chatId,
          previousCount: current,
          newCount: current + 1
        });
      }
    },
    clearUnread(state, action) {
      const chatId = action.payload;
      if (!chatId) return;
      delete state.unread[chatId];
      const idx = state.groupChats.findIndex((c) => {
        const id = String(chatId);
        return (
          String(c._id) === id ||
          String(c.chatRequestId) === id ||
          String(c.groupId) === id
        );
      });
      if (idx > -1) {
        state.groupChats[idx].unreadCount = 0;
      }
    },
    clearPendingForChat(state, action) {
      const { chatId, userId } = action.payload || {};
      if (!chatId || !userId) return;

      const idx = state.groupChats.findIndex((c) => {
        const id = String(chatId);
        return (
          String(c._id) === id ||
          String(c.chatRequestId) === id ||
          String(c.groupId) === id
        );
      });

      if (idx > -1) {
        const chat = state.groupChats[idx];
        if (Array.isArray(chat.pendingMembers)) {
          const pmIdx = chat.pendingMembers.findIndex((m) => String(m.userId) === String(userId));
          console.log("<>>>>>>>>>>>>>>>>>>><<<<<<<<<", pmIdx)
          if (pmIdx > -1) {
            chat.pendingMembers.splice(pmIdx, 1);
          }
        }
      }
    },
    addPendingMessage: (state, action) => {
      const message = action.payload;
      const chatId = message.chatId || message.chatRequestId;

      if (!chatId) return;

      if (state.activeChatId === String(chatId)) {
        const exists = state.currentChat.messages?.some(msg =>
          msg._id === message._id
        );
        if (!exists) {
          state.currentChat.messages = [
            ...(state.currentChat.messages || []),
            message
          ];
        }
      } else {
        const chatIndex = state.groupChats.findIndex(chat =>
          String(chat._id) === String(chatId) ||
          String(chat.chatRequestId) === String(chatId)
        );

        if (chatIndex > -1) {
          const currentCount = state.groupChats[chatIndex].unreadCount || 0;
          state.groupChats[chatIndex].unreadCount = currentCount + 1;

          console.log("📈 [REDUCER] Adding pending message, incremented unread:", {
            chatId: chatId,
            previousCount: currentCount,
            newCount: currentCount + 1
          });

          state.groupChats[chatIndex].lastMessage = message;
          state.groupChats[chatIndex].lastMessageTimestamp =
            message.createdAt || new Date().toISOString();

          const [updatedChat] = state.groupChats.splice(chatIndex, 1);
          state.groupChats.unshift(updatedChat);
        }
      }
    },
    updateGroupChatLastMessage(state, action) {
      const { chatId, lastMessage, lastMessageTimestamp } = action.payload;

      if (!chatId || !lastMessage) return;

      const groupIndex = state.groupChats.findIndex(chat => {
        const id = String(chatId);
        return (
          String(chat._id) === id ||
          String(chat.chatRequestId) === id ||
          String(chat.groupId) === id
        );
      });

      if (groupIndex > -1) {
        state.groupChats[groupIndex] = {
          ...state.groupChats[groupIndex],
          lastMessage: {
            ...state.groupChats[groupIndex].lastMessage,
            ...lastMessage
          },
          lastMessageTimestamp: lastMessageTimestamp || state.groupChats[groupIndex].lastMessageTimestamp
        };

        const [updatedChat] = state.groupChats.splice(groupIndex, 1);
        state.groupChats.unshift(updatedChat);
      }
    },
    hydrateChatFromCache(state, action) {
      const chatId = action.payload;
      if (!chatId || !state.messageCache?.[String(chatId)]?.length) return;

      const cachedMessages = state.messageCache[String(chatId)];
      const meta =
        state.groupChats.find((c) => {
          const id = String(chatId);
          return (
            String(c._id) === id ||
            String(c.chatRequestId) === id ||
            String(c.groupId) === id
          );
        }) || {};

      state.currentChat = {
        ...state.currentChat,
        ...meta,
        _id: meta._id || chatId,
        chatRequestId: meta.chatRequestId || chatId,
        messages: cachedMessages,
        members: meta.members || state.currentChat.members || [],
        hasMore: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all groups with pagination
      .addCase(fetchAllGroupChats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllGroupChats.fulfilled, (state, action) => {
        state.loading = false;
        const { chats = [], page, hasMore, totalData } = action.payload || {};

        // ✅ FIXED: Deduplicate groups to prevent duplicate listings
        const uniqueChats = [];
        const seenIds = new Set();

        for (const chat of chats) {
          const id = String(chat._id || chat.chatRequestId);
          if (!seenIds.has(id)) {
            seenIds.add(id);
            uniqueChats.push(chat);
          }
        }

        // If page 1, replace; otherwise append (with deduplication)
        if (page === 1) {
          state.groupChats = uniqueChats;
        } else {
          const existingIds = new Set(state.groupChats.map(c => String(c._id || c.chatRequestId)));
          const newChats = uniqueChats.filter(c => !existingIds.has(String(c._id || c.chatRequestId)));
          state.groupChats = [...state.groupChats, ...newChats];
        }

        state.groupsPage = page;
        state.hasMoreGroups = hasMore;
        if (totalData !== undefined) state.totalGroups = totalData;
      })
      .addCase(fetchAllGroupChats.rejected, (state, action) => {
        state.loading = false;
        const errorMsg = action.payload;
        if (errorMsg && !errorMsg.includes("No groups")) {
          state.error = errorMsg || "Failed to fetch group chats";
        } else {
          state.error = null;
          if (action.meta?.arg?.page === 1) {
            state.groupChats = [];
          }
        }
      })

      // Fetch chat messages with dynamic limit
      .addCase(fetchChatById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatById.fulfilled, (state, action) => {
        state.loading = false;

        const payloadMessages = action.payload?.messages || [];
        const chatId = String(
          action.payload._id ||
          action.payload.chatRequestId ||
          action.meta?.arg?.chatId ||
          action.meta?.arg || ''
        );

        const cachedMessages = chatId && state.messageCache?.[chatId]
          ? state.messageCache[chatId]
          : [];

        const mergedMessages = mergeMessagesLatestFirst(payloadMessages, cachedMessages);

        if (chatId) {
          if (state.unread && state.unread[chatId] !== undefined) {
            delete state.unread[chatId];
          }
          const idx = state.groupChats.findIndex((c) => {
            return (
              String(c._id) === chatId ||
              String(c.chatRequestId) === chatId ||
              String(c.groupId) === chatId
            );
          });
          if (idx > -1) {
            state.groupChats[idx].unreadCount = 0;
          }

          upsertMessageCache(state, chatId, mergedMessages);
        }

        state.currentChat = {
          messages: [],
          members: [],
          page: 1,
          hasMore: true,
          ...action.payload,
          messages: mergedMessages,
        };
      })
      .addCase(fetchChatById.rejected, (state, action) => {
        state.loading = false;
        const errorMsg = action.payload || "Failed to fetch chat";
        if (!errorMsg.includes("not found") && !errorMsg.includes("not accepted")) {
          state.error = errorMsg;
        } else {
          state.error = "Chat not available. Please select another group.";
        }
      })

      // Load more messages with pagination
      .addCase(fetchMoreMessages.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMoreMessages.fulfilled, (state, action) => {
        state.loading = false;
        const { messages, page, hasMore, chatId } = action.payload;

        console.log("🔄 Updating state with new messages:", messages.length);

        if (state.currentChat && String(state.currentChat._id) === String(chatId)) {
          const existingIds = new Set(
            (state.currentChat.messages || []).map(m => String(m._id))
          );
          const newMessages = messages.filter(m => !existingIds.has(String(m._id)));

          state.currentChat.messages = [...(state.currentChat.messages || []), ...newMessages];

          const resolvedChatId =
            state.currentChat._id ||
            state.currentChat.chatRequestId ||
            state.currentChat.groupId;

          if (resolvedChatId && newMessages.length) {
            upsertMessageCache(state, resolvedChatId, newMessages);
          }
          state.currentChat.page = page;
          state.currentChat.hasMore = hasMore;
          console.log("✅ Messages updated. Total:", state.currentChat.messages.length);
        }
      })
      .addCase(fetchMoreMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error("❌ Failed to load more messages:", action.payload);
      })

      // Create group
      .addCase(createGroupChat.fulfilled, (state, action) => {
        if (action.payload) state.groupChats.push(action.payload);
      })

      // Send message
      .addCase(sendChatMessage.pending, (state, action) => {
        const { content } = action.meta.arg || {};
        const tmpId = action.meta.requestId;

        let me = { _id: "me" };
        try {
          const authRaw = typeof window !== 'undefined' ? localStorage.getItem('authUser') : null;
          if (authRaw) {
            const auth = JSON.parse(authRaw);
            const u = auth?.user || auth || {};
            me = {
              _id: u._id || "me",
              firstname: u.firstname || u.firstName || "",
              lastname: u.lastname || u.lastName || "",
              profileimg: u.profileimg || u.avatar || "",
            };
          }
        } catch { }

        const tempMsg = {
          _id: tmpId,
          _tmp: true,
          content,
          sender: me,
          createdAt: new Date().toISOString(),
          status: "pending",
        };

        const existingMessages = state.currentChat.messages || [];
        state.currentChat = {
          ...state.currentChat,
          messages: [tempMsg, ...existingMessages],
        };

        const resolvedChatId =
          state.currentChat?._id ||
          state.currentChat?.chatRequestId ||
          state.currentChat?.groupId;

        if (resolvedChatId) {
          upsertMessageCache(state, resolvedChatId, [tempMsg]);
        }
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        const serverMsg = action.payload;
        const tmpId = action.meta.requestId;
        if (!serverMsg) return;

        const messages = state.currentChat.messages || [];

        const idx = messages.findIndex((m) => m._id === tmpId || m._tmp === true);

        if (idx !== -1) {
          const preservedSender = messages[idx].sender || { _id: "me" };
          const serverSender = serverMsg.sender || {};
          const mergedSender = {
            _id: serverSender._id || preservedSender._id,
            firstname: serverSender.firstname ?? preservedSender.firstname,
            lastname: serverSender.lastname ?? preservedSender.lastname,
            email: serverSender.email ?? preservedSender.email,
            profileimg: (serverSender.profileimg && String(serverSender.profileimg).trim() !== "")
              ? serverSender.profileimg
              : preservedSender.profileimg,
          };

          messages[idx] = {
            ...serverMsg,
            status: "sent",
            sender: mergedSender,
            _tmp: undefined
          };
        } else {
          messages.unshift({ ...serverMsg, status: "sent" });
        }

        state.currentChat.messages = messages;

        const resolvedChatId =
          state.currentChat?._id ||
          state.currentChat?.chatRequestId ||
          state.currentChat?.groupId;

        if (resolvedChatId) {
          const updatedMessage = idx !== -1 ? messages[idx] : messages[0];
          upsertMessageCache(state, resolvedChatId, [updatedMessage]);
        }

        if (resolvedChatId) {
          const groupIndex = state.groupChats.findIndex(chat =>
            String(chat._id) === String(resolvedChatId) ||
            String(chat.chatRequestId) === String(resolvedChatId)
          );

          if (groupIndex > -1) {
            const [updatedChat] = state.groupChats.splice(groupIndex, 1);
            state.groupChats.unshift(updatedChat);
          }
        }
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        const tmpId = action.meta.requestId;
        const messages = state.currentChat.messages || [];
        const idx = messages.findIndex((m) => m._id === tmpId);
        if (idx !== -1) {
          messages[idx].status = "failed";
          messages[idx].error = action.payload || action.error?.message;
        } else {
          state.error = action.payload || action.error?.message;
        }
      })
      .addCase(editChatMessage.fulfilled, (state, action) => {
        const { chatId, messageId, updatedMessage } = action.payload;
        if (!state.currentChat || String(state.currentChat._id) !== String(chatId)) return;

        const idx = state.currentChat.messages.findIndex(m => String(m._id) === String(messageId));
        if (idx !== -1) {
          state.currentChat.messages[idx] = {
            ...state.currentChat.messages[idx],
            ...updatedMessage,
            content: updatedMessage.content || state.currentChat.messages[idx].content,
            isEdited: true,
            editedAt: updatedMessage.editedAt || new Date().toISOString()
          };
        }
      })
      .addCase(editChatMessage.rejected, (state, action) => {
        state.error = action.payload || "Failed to edit message";
      })
      .addCase(deleteChatMessage.fulfilled, (state, action) => {
        const { chatId, deletedMessageIds = [] } = action.payload;

        if (state.currentChat && String(state.currentChat._id) === String(chatId)) {
          state.currentChat.messages = state.currentChat.messages.map(msg =>
            deletedMessageIds.includes(String(msg._id))
              ? {
                ...msg,
                isDeleted: true,
                isDeleteEvery: action.meta?.arg?.payload?.deleteFor === 'everyone',
                isDeleteMe: action.meta?.arg?.payload?.deleteFor === 'me'
              }
              : msg
          );
        }

        state.groupChats = state.groupChats.map(chat => {
          if (String(chat._id) === String(chatId) && chat.lastMessage) {
            const lastMessageId = chat.lastMessage._id || chat.lastMessage.messageId;
            if (deletedMessageIds.includes(String(lastMessageId))) {
              return {
                ...chat,
                lastMessage: {
                  ...chat.lastMessage,
                  isDeleted: true,
                  content: "This message was deleted"
                }
              };
            }
          }
          return chat;
        });
      });
  },
});

export const {
  clearChatError,
  clearCurrentChat,
  setActiveChatId,
  setTypingUser,
  addNewMessage,
  updateMessages,
  updateChatListOrder,
  incrementUnread,
  clearUnread,
  clearPendingForChat,
  updateGroupChatLastMessage,
  hydrateChatFromCache
} = chatSlice.actions;

export default chatSlice.reducer;